import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import VerificationPanel from './VerificationPanel'
import { FundUseForm } from '../parish/GivingSettings'
import {
  ArrowLeft,
  Loader2,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  DollarSign,
  Users,
  Calendar,
  TrendingUp,
  CreditCard,
  CheckCircle2,
  XCircle,
  ChevronUp,
  ChevronDown,
  Clock,
  Plus,
  Trash2,
  Upload,
  X,
  Check,
  Pencil
} from 'lucide-react'

const VISIBLE_DONATION_COUNT = 3

export default function AdminParishDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isSuperAdmin } = useUserRole()
  const [parish, setParish] = useState(null)
  const [loading, setLoading] = useState(true)
  const [verificationRefreshKey, setVerificationRefreshKey] = useState(0)
  const [error, setError] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [dateRange, setDateRange] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [customRangeSubmitKey, setCustomRangeSubmitKey] = useState(0)
  const [donationTypeFilter, setDonationTypeFilter] = useState('all')
  const [stripeStatus, setStripeStatus] = useState(null)
  const [stripeStatusLoading, setStripeStatusLoading] = useState(false)
  const [showAllDonations, setShowAllDonations] = useState(false)

  // Edit mode state
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(null)
  const [editData, setEditData] = useState({
    name: '', address: '', city: '', state: '', zipCode: '',
    email: '', phone: '', description: '', briefBio: '',
  })

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  // Parish info state
  const [massTimes, setMassTimes] = useState([])
  const [confessionTimes, setConfessionTimes] = useState([])
  const [parishStaff, setParishStaff] = useState([])
  const [savingInfo, setSavingInfo] = useState(null)

  // Migrate legacy flat format [{ day, time }] to grouped format [{ day, times: [{ time, note }] }]
  const migrateMassTimes = (data) => {
    if (!Array.isArray(data) || data.length === 0) return data
    if (data[0].times) return data
    const grouped = {}
    data.forEach(entry => {
      if (!grouped[entry.day]) grouped[entry.day] = []
      grouped[entry.day].push({ time: entry.time || '', note: '' })
    })
    return Object.entries(grouped).map(([day, times]) => ({ day, times }))
  }

  const migrateConfessionTimes = (data) => {
    if (!Array.isArray(data) || data.length === 0) return data
    if (data[0].times) return data
    const grouped = {}
    data.forEach(entry => {
      if (!grouped[entry.day]) grouped[entry.day] = []
      grouped[entry.day].push({ startTime: entry.time || '', endTime: '', note: '' })
    })
    return Object.entries(grouped).map(([day, times]) => ({ day, times }))
  }

  const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  useEffect(() => {
    if (!isSuperAdmin) {
      setError('You do not have permission to access this page.')
      setLoading(false)
      return
    }
    if (id) {
      fetchParish()
      fetchDashboardData()
      fetchStripeStatus()
    }
  }, [id, isSuperAdmin, dateRange, customRangeSubmitKey, donationTypeFilter])

  const fetchParish = async () => {
    try {
      setLoading(true)
      setError(null)
      const parishData = await api.getParish(id)
      setParish(parishData)
    } catch (err) {
      console.error('Failed to fetch parish:', err)
      setError(err.message || 'Failed to load parish')
    } finally {
      setLoading(false)
    }
  }

  // Re-read without toggling the page-level loading state (which would unmount the page).
  const refreshParishQuietly = async () => {
    try {
      setParish(await api.getParish(id))
    } catch (err) {
      console.error('Failed to refresh parish:', err)
    }
    setVerificationRefreshKey((k) => k + 1)
  }

  const getDateRangeParams = () => {
    if (dateRange === 'all') {
      return {}
    } else if (dateRange === 'custom') {
      return {
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
      }
    } else {
      const now = new Date()
      let start
      
      if (dateRange === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      } else if (dateRange === 'week') {
        start = new Date(now)
        start.setDate(now.getDate() - now.getDay())
        start.setHours(0, 0, 0, 0)
      } else if (dateRange === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1)
      } else if (dateRange === 'year') {
        start = new Date(now.getFullYear(), 0, 1)
      } else {
        return {}
      }
      
      return {
        startDate: start.toISOString(),
        endDate: now.toISOString(),
      }
    }
  }

  const fetchDashboardData = async () => {
    if (!id) return

    try {
      setDashboardLoading(true)
      const dateParams = getDateRangeParams()
      const dashboardData = await api.getParishDonationDashboard(id, {
        ...dateParams,
        donationType: donationTypeFilter !== 'all' ? donationTypeFilter : undefined,
      })
      setDashboard(dashboardData)
    } catch (err) {
      console.error('Failed to fetch donation dashboard:', err)
      setError(err.message || 'Failed to load donation dashboard')
    } finally {
      setDashboardLoading(false)
    }
  }

  const fetchStripeStatus = async () => {
    if (!id) return

    try {
      setStripeStatusLoading(true)
      const status = await api.stripeStatus(id)
      setStripeStatus(status)
    } catch (err) {
      console.error('Failed to fetch Stripe status:', err)
      // Don't set error for Stripe status, just log it
    } finally {
      setStripeStatusLoading(false)
    }
  }

  // Initialize edit state from parish data
  const initEditState = (parishData) => {
    setEditData({
      name: parishData.name || '',
      address: parishData.address || '',
      city: parishData.city || '',
      state: parishData.state || '',
      zipCode: parishData.zipCode || '',
      email: parishData.email || '',
      phone: parishData.phoneNumber || '',
      description: parishData.description || '',
      briefBio: parishData.briefBio || '',
    })
    setImagePreview(parishData.imageUrl || null)
    const info = parishData.parishInfo || {}
    setMassTimes(migrateMassTimes(info.massTimes || []))
    setConfessionTimes(migrateConfessionTimes(info.confessionTimes || []))
    setParishStaff(info.parishStaff || [])
  }

  // Called after parish is fetched
  useEffect(() => {
    if (parish) initEditState(parish)
  }, [parish])

  const handleEditInputChange = (e) => {
    const { name, value } = e.target
    setEditData(prev => ({ ...prev, [name]: value }))
    if (success) setSuccess(null)
  }

  const handleStartEditing = () => {
    if (parish) initEditState(parish)
    setEditing(true)
  }

  const handleCancelEditing = () => {
    if (parish) initEditState(parish)
    setEditing(false)
    setSelectedImage(null)
  }

  const handleSaveBasicInfo = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      const updateData = {}
      if (editData.name !== (parish.name || '')) updateData.name = editData.name
      if (editData.address !== (parish.address || '')) updateData.address = editData.address
      if (editData.city !== (parish.city || '')) updateData.city = editData.city
      if (editData.state !== (parish.state || '')) updateData.state = editData.state
      if (editData.zipCode !== (parish.zipCode || '')) updateData.zipCode = editData.zipCode
      if (editData.email !== (parish.email || '')) updateData.email = editData.email
      if (editData.phone !== (parish.phoneNumber || '')) updateData.phone = editData.phone
      if (editData.description !== (parish.description || '')) updateData.description = editData.description
      if (editData.briefBio !== (parish.briefBio || '')) updateData.briefBio = editData.briefBio

      if (Object.keys(updateData).length === 0) {
        setSuccess('No changes to save')
        setTimeout(() => setSuccess(null), 3000)
        setSaving(false)
        return
      }

      const updatedParish = await api.updateParish(id, updateData)
      setParish(updatedParish)
      setEditing(false)
      setSuccess('Parish information updated successfully!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update parish:', err)
      setError(err.message || 'Failed to update parish information')
    } finally {
      setSaving(false)
    }
  }

  // Image upload
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, WebP, or GIF image.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB. Please select a smaller image.')
      return
    }
    setSelectedImage(file)
    setError(null)
    const reader = new FileReader()
    reader.onloadend = () => setImagePreview(reader.result)
    reader.readAsDataURL(file)
  }

  const handleRemoveSelectedImage = () => {
    setSelectedImage(null)
    setImagePreview(parish?.imageUrl || null)
  }

  const handleImageUpload = async () => {
    if (!selectedImage || !id) return
    try {
      setUploadingImage(true)
      setError(null)
      setSuccess(null)
      const updatedParish = await api.uploadParishImage(id, selectedImage)
      setParish(updatedParish)
      setImagePreview(updatedParish.imageUrl)
      setSelectedImage(null)
      setSuccess('Image uploaded successfully!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to upload image:', err)
      setError(err.message || 'Failed to upload image')
    } finally {
      setUploadingImage(false)
    }
  }

  // Parish info save handlers
  const saveParishInfoSection = async (key, value, section) => {
    if (!id) return
    try {
      setSavingInfo(section)
      setError(null)
      setSuccess(null)
      await api.updateParish(id, { [key]: value })
      const parishData = await api.getParish(id)
      setParish(parishData)
      setSuccess(`${section === 'mass' ? 'Mass times' : section === 'confession' ? 'Confession times' : 'Parish staff'} saved successfully!`)
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error(`Failed to save ${section}:`, err)
      setError(err.message || `Failed to save ${section}`)
    } finally {
      setSavingInfo(null)
    }
  }

  // Mass times helpers (grouped by day)
  const addMassTimeDay = () => setMassTimes(prev => [...prev, { day: 'Sunday', times: [{ time: '', note: '' }] }])
  const removeMassTimeDay = (dayIndex) => setMassTimes(prev => prev.filter((_, i) => i !== dayIndex))
  const updateMassTimeDay = (dayIndex, day) => {
    setMassTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, day } : item))
    if (success) setSuccess(null)
  }
  const addMassTimeSlot = (dayIndex) => {
    setMassTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, times: [...item.times, { time: '', note: '' }] } : item))
  }
  const removeMassTimeSlot = (dayIndex, slotIndex) => {
    setMassTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.filter((_, si) => si !== slotIndex)
      return { ...item, times: newTimes }
    }))
  }
  const updateMassTimeSlot = (dayIndex, slotIndex, field, value) => {
    setMassTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.map((slot, si) => si === slotIndex ? { ...slot, [field]: value } : slot)
      return { ...item, times: newTimes }
    }))
    if (success) setSuccess(null)
  }

  // Confession times helpers (grouped by day)
  const addConfessionTimeDay = () => setConfessionTimes(prev => [...prev, { day: 'Saturday', times: [{ startTime: '', endTime: '', note: '' }] }])
  const removeConfessionTimeDay = (dayIndex) => setConfessionTimes(prev => prev.filter((_, i) => i !== dayIndex))
  const updateConfessionTimeDay = (dayIndex, day) => {
    setConfessionTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, day } : item))
    if (success) setSuccess(null)
  }
  const addConfessionTimeSlot = (dayIndex) => {
    setConfessionTimes(prev => prev.map((item, i) => i === dayIndex ? { ...item, times: [...item.times, { startTime: '', endTime: '', note: '' }] } : item))
  }
  const removeConfessionTimeSlot = (dayIndex, slotIndex) => {
    setConfessionTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.filter((_, si) => si !== slotIndex)
      return { ...item, times: newTimes }
    }))
  }
  const updateConfessionTimeSlot = (dayIndex, slotIndex, field, value) => {
    setConfessionTimes(prev => prev.map((item, i) => {
      if (i !== dayIndex) return item
      const newTimes = item.times.map((slot, si) => si === slotIndex ? { ...slot, [field]: value } : slot)
      return { ...item, times: newTimes }
    }))
    if (success) setSuccess(null)
  }

  // Parish staff helpers
  const addStaffMember = () => setParishStaff(prev => [...prev, { title: '', name: '', email: '', phone: '' }])
  const removeStaffMember = (index) => setParishStaff(prev => prev.filter((_, i) => i !== index))
  const updateStaffMember = (index, field, value) => {
    setParishStaff(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item))
    if (success) setSuccess(null)
  }

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatAddress = () => {
    if (!parish) return null
    const parts = [parish.address, parish.city, parish.state, parish.zipCode].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }

  const getGoogleMapsUrl = () => {
    if (!parish) return null
    const address = formatAddress()
    if (!address) return null
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  }

  const handleCustomRangeSubmit = () => {
    setCustomRangeSubmitKey(prev => prev + 1)
  }

  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-6">
        <p className="text-red-600">You do not have permission to access this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error && !parish) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-6">
        <p className="text-red-600">{error}</p>
        <Button
          variant="outline"
          onClick={() => navigate('/app/admin/parishes')}
          className="mt-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parishes
        </Button>
      </div>
    )
  }

  if (!parish) {
    return (
      <div className="bg-white border border-surface-border rounded p-8 text-center">
        <p className="text-charcoal-muted mb-4">Parish not found</p>
        <Button
          variant="outline"
          onClick={() => navigate('/app/admin/parishes')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parishes
        </Button>
      </div>
    )
  }

  const address = formatAddress()
  const googleMapsUrl = getGoogleMapsUrl()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/app/admin/parishes')}
          className="text-charcoal hover:text-charcoal-muted mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parishes
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal">{parish.name}</h1>
          <p className="text-charcoal-muted">Parish Details & Donation Dashboard</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded flex items-center gap-2">
          <Check className="w-5 h-5 text-green-400" />
          <p className="text-green-400 text-sm">{success}</p>
        </div>
      )}

      {/* Cover Image */}
      {parish.imageUrl && (
        <div className="relative w-full h-64 md:h-96 rounded overflow-hidden">
          <img
            src={parish.imageUrl}
            alt={parish.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-background-dark/50 to-transparent" />
        </div>
      )}

      {/* Image Upload */}
      <div className="bg-white border border-surface-border rounded p-6">
        <h2 className="text-xl font-semibold font-display text-charcoal mb-4">Parish Image</h2>
        <div className="space-y-4">
          {imagePreview && (
            <div className="relative w-full max-w-md">
              <img
                src={imagePreview}
                alt="Parish preview"
                className="w-full h-64 object-cover rounded border border-surface-border"
              />
              {selectedImage && (
                <button
                  type="button"
                  onClick={handleRemoveSelectedImage}
                  className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 rounded-full text-white"
                  title="Remove selected image"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          <div className="flex items-center gap-4">
            <Label htmlFor="admin-image-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 bg-surface-linen border border-gold/30 rounded text-charcoal hover:bg-surface-border transition-colors">
                <Upload className="w-4 h-4" />
                <span>{selectedImage ? 'Change Image' : 'Upload Image'}</span>
              </div>
              <input
                id="admin-image-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                onChange={handleImageSelect}
                className="hidden"
              />
            </Label>
            {selectedImage && (
              <Button
                type="button"
                onClick={handleImageUpload}
                disabled={uploadingImage}
                className="bg-gold text-black hover:bg-gold/90"
              >
                {uploadingImage ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload</>
                )}
              </Button>
            )}
          </div>
          <p className="text-sm text-charcoal-muted">
            Upload a cover image for this parish (JPEG, PNG, WebP, or GIF, max 5MB)
          </p>
        </div>
      </div>

      {/* Stripe Status */}
      <div className="bg-white border border-surface-border rounded p-6">
        <h2 className="text-2xl font-bold font-display text-charcoal mb-4">Stripe Connect Status</h2>
        {stripeStatusLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gold animate-spin" />
          </div>
        ) : stripeStatus ? (
          <div className="space-y-4">
            <div>
              {!stripeStatus.isConnected ? (
                <p className="text-amber-400/90 font-medium text-lg">Not connected</p>
              ) : (
                <p
                  className={`text-lg font-semibold ${
                    stripeStatus.chargesEnabled && stripeStatus.payoutsEnabled
                      ? 'text-green-400'
                      : 'text-amber-400/90'
                  }`}
                >
                  {stripeStatus.chargesEnabled && stripeStatus.payoutsEnabled ? 'Active' : 'Pending'}
                </p>
              )}
            </div>

            {stripeStatus.isConnected && (
              <div className="space-y-4 bg-surface-linen/50 rounded p-4 border border-surface-border">
                <div className="flex justify-between items-center py-2 border-b border-surface-border">
                  <span className="text-sm font-medium text-charcoal-muted">Payouts enabled</span>
                  {stripeStatus.payoutsEnabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600/80" />
                  )}
                </div>
                <div className="flex justify-between items-center py-2 border-b border-surface-border">
                  <span className="text-sm font-medium text-charcoal-muted">Charges enabled</span>
                  {stripeStatus.chargesEnabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600/80" />
                  )}
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm font-medium text-charcoal-muted">Details submitted</span>
                  {stripeStatus.detailsSubmitted ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600/80" />
                  )}
                </div>
              </div>
            )}

            {stripeStatus.chargesEnabled && stripeStatus.payoutsEnabled && (
              <p className="text-green-400/90 text-sm">
                Stripe account is fully set up and can receive payouts from donations.
              </p>
            )}
          </div>
        ) : (
          <p className="text-charcoal-muted text-sm">Unable to load Stripe status.</p>
        )}
      </div>

      {/* Verification */}
      <div className="bg-white border border-surface-border rounded p-6">
        <h2 className="text-2xl font-bold font-display text-charcoal mb-4">Organization Verification</h2>
        <VerificationPanel parishId={id} refreshKey={verificationRefreshKey} />
      </div>

      {/* Fund use disclosure */}
      <div className="bg-white border border-surface-border rounded p-6">
        <h2 className="text-2xl font-bold font-display text-charcoal mb-4">How Donations Are Described</h2>
        <FundUseForm parish={parish} onSaved={refreshParishQuietly} />
      </div>

      {/* Parish Information */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold font-display text-charcoal">Parish Information</h2>
          {!editing && (
            <Button variant="outline" onClick={handleStartEditing}>
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="edit-name" className="text-charcoal">Parish Name *</Label>
                <Input id="edit-name" name="name" value={editData.name} onChange={handleEditInputChange} required className="mt-1 bg-surface-linen border-surface-border text-charcoal" placeholder="St. Mary's Catholic Church" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="edit-address" className="text-charcoal">Street Address</Label>
                <Input id="edit-address" name="address" value={editData.address} onChange={handleEditInputChange} className="mt-1 bg-surface-linen border-surface-border text-charcoal" placeholder="123 Main Street" />
              </div>
              <div>
                <Label htmlFor="edit-city" className="text-charcoal">City</Label>
                <Input id="edit-city" name="city" value={editData.city} onChange={handleEditInputChange} className="mt-1 bg-surface-linen border-surface-border text-charcoal" placeholder="New York" />
              </div>
              <div>
                <Label htmlFor="edit-state" className="text-charcoal">State</Label>
                <Input id="edit-state" name="state" value={editData.state} onChange={handleEditInputChange} maxLength={2} className="mt-1 bg-surface-linen border-surface-border text-charcoal uppercase" placeholder="NY" style={{ textTransform: 'uppercase' }} />
              </div>
              <div>
                <Label htmlFor="edit-zipCode" className="text-charcoal">ZIP Code</Label>
                <Input id="edit-zipCode" name="zipCode" value={editData.zipCode} onChange={handleEditInputChange} className="mt-1 bg-surface-linen border-surface-border text-charcoal" placeholder="10001" />
              </div>
              <div>
                <Label htmlFor="edit-email" className="text-charcoal">Email</Label>
                <Input id="edit-email" name="email" type="email" value={editData.email} onChange={handleEditInputChange} className="mt-1 bg-surface-linen border-surface-border text-charcoal" placeholder="info@stmarys.org" />
              </div>
              <div>
                <Label htmlFor="edit-phone" className="text-charcoal">Phone</Label>
                <Input id="edit-phone" name="phone" type="tel" value={editData.phone} onChange={handleEditInputChange} className="mt-1 bg-surface-linen border-surface-border text-charcoal" placeholder="+1-555-123-4567" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="edit-briefBio" className="text-charcoal">Brief Bio</Label>
                <textarea id="edit-briefBio" name="briefBio" value={editData.briefBio} onChange={handleEditInputChange} rows={2} maxLength={500} className="mt-1 w-full px-3 py-2 bg-surface-linen border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50" placeholder="A brief bio for the parish" />
                <p className="mt-1 text-xs text-charcoal-muted">{editData.briefBio.length}/500 characters</p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="edit-description" className="text-charcoal">Description</Label>
                <textarea id="edit-description" name="description" value={editData.description} onChange={handleEditInputChange} rows={4} maxLength={2000} className="mt-1 w-full px-3 py-2 bg-surface-linen border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50" placeholder="A detailed description of the parish" />
                <p className="mt-1 text-xs text-charcoal-muted">{editData.description.length}/2000 characters</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-surface-border">
              <Button type="button" variant="outline" onClick={handleCancelEditing} disabled={saving}>Cancel</Button>
              <Button type="button" onClick={handleSaveBasicInfo} disabled={saving} className="bg-gold text-black hover:bg-gold/90">
                {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {parish.briefBio && (
                <div>
                  <h3 className="text-sm font-medium text-charcoal-muted mb-1">Brief Bio</h3>
                  <p className="text-charcoal/90">{parish.briefBio}</p>
                </div>
              )}
              {parish.description && (
                <div>
                  <h3 className="text-sm font-medium text-charcoal-muted mb-1">Description</h3>
                  <p className="text-charcoal/90 whitespace-pre-wrap">{parish.description}</p>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-charcoal mb-2">Contact Information</h3>
              {address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-charcoal-muted mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-charcoal/90">{address}</p>
                    {googleMapsUrl && (
                      <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-charcoal-muted hover:text-charcoal mt-1 inline-flex items-center gap-1">
                        View on Google Maps
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              )}
              {parish.phoneNumber && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-charcoal-muted flex-shrink-0" />
                  <a href={`tel:${parish.phoneNumber}`} className="text-charcoal/90 hover:text-charcoal">{parish.phoneNumber}</a>
                </div>
              )}
              {parish.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-charcoal-muted flex-shrink-0" />
                  <a href={`mailto:${parish.email}`} className="text-charcoal/90 hover:text-charcoal break-all">{parish.email}</a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mass Times Section */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-gold" />
          <h2 className="text-xl font-semibold font-display text-charcoal">Mass Times</h2>
        </div>

        {massTimes.length === 0 ? (
          <p className="text-charcoal-muted text-sm mb-4">No mass times added yet.</p>
        ) : (
          <div className="space-y-4 mb-4">
            {massTimes.map((dayGroup, dayIndex) => (
              <div key={dayIndex} className="border border-surface-border rounded p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <select
                    value={dayGroup.day}
                    onChange={(e) => updateMassTimeDay(dayIndex, e.target.value)}
                    className="h-10 rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm font-semibold text-charcoal focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold w-44"
                  >
                    {DAYS_OF_WEEK.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeMassTimeDay(dayIndex)} className="p-1 text-red-500 hover:text-red-700 transition-colors" title="Remove day">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2 pl-2">
                  {dayGroup.times.map((slot, slotIndex) => (
                    <div key={slotIndex} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={slot.time}
                        onChange={(e) => updateMassTimeSlot(dayIndex, slotIndex, 'time', e.target.value)}
                        placeholder="e.g. 9:00 AM"
                        className="w-36 bg-surface-linen border-surface-border text-charcoal"
                      />
                      <Input
                        type="text"
                        value={slot.note || ''}
                        onChange={(e) => updateMassTimeSlot(dayIndex, slotIndex, 'note', e.target.value)}
                        placeholder="Note (optional)"
                        className="flex-1 bg-surface-linen border-surface-border text-charcoal"
                      />
                      <button type="button" onClick={() => removeMassTimeSlot(dayIndex, slotIndex)} className="p-1.5 text-red-500 hover:text-red-700 transition-colors" title="Remove time">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addMassTimeSlot(dayIndex)} className="text-sm text-gold hover:text-gold/80 flex items-center gap-1 mt-1">
                    <Plus className="w-3.5 h-3.5" />Add time
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-surface-border">
          <Button type="button" variant="outline" onClick={addMassTimeDay}>
            <Plus className="w-4 h-4 mr-2" />Add Day
          </Button>
          <Button type="button" onClick={() => saveParishInfoSection('massTimes', massTimes, 'mass')} disabled={savingInfo === 'mass'} className="bg-gold text-black hover:bg-gold/90">
            {savingInfo === 'mass' ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : 'Save Mass Times'}
          </Button>
        </div>
      </div>

      {/* Confession Times Section */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-gold" />
          <h2 className="text-xl font-semibold font-display text-charcoal">Confession Times</h2>
        </div>

        {confessionTimes.length === 0 ? (
          <p className="text-charcoal-muted text-sm mb-4">No confession times added yet.</p>
        ) : (
          <div className="space-y-4 mb-4">
            {confessionTimes.map((dayGroup, dayIndex) => (
              <div key={dayIndex} className="border border-surface-border rounded p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <select
                    value={dayGroup.day}
                    onChange={(e) => updateConfessionTimeDay(dayIndex, e.target.value)}
                    className="h-10 rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm font-semibold text-charcoal focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold w-44"
                  >
                    {DAYS_OF_WEEK.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeConfessionTimeDay(dayIndex)} className="p-1 text-red-500 hover:text-red-700 transition-colors" title="Remove day">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2 pl-2">
                  {dayGroup.times.map((slot, slotIndex) => (
                    <div key={slotIndex} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={slot.startTime}
                        onChange={(e) => updateConfessionTimeSlot(dayIndex, slotIndex, 'startTime', e.target.value)}
                        placeholder="Start (e.g. 3:00 PM)"
                        className="w-40 bg-surface-linen border-surface-border text-charcoal"
                      />
                      <span className="text-charcoal-muted text-sm">to</span>
                      <Input
                        type="text"
                        value={slot.endTime}
                        onChange={(e) => updateConfessionTimeSlot(dayIndex, slotIndex, 'endTime', e.target.value)}
                        placeholder="End (e.g. 4:00 PM)"
                        className="w-40 bg-surface-linen border-surface-border text-charcoal"
                      />
                      <Input
                        type="text"
                        value={slot.note || ''}
                        onChange={(e) => updateConfessionTimeSlot(dayIndex, slotIndex, 'note', e.target.value)}
                        placeholder="Note (optional)"
                        className="flex-1 bg-surface-linen border-surface-border text-charcoal"
                      />
                      <button type="button" onClick={() => removeConfessionTimeSlot(dayIndex, slotIndex)} className="p-1.5 text-red-500 hover:text-red-700 transition-colors" title="Remove time">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addConfessionTimeSlot(dayIndex)} className="text-sm text-gold hover:text-gold/80 flex items-center gap-1 mt-1">
                    <Plus className="w-3.5 h-3.5" />Add time slot
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-surface-border">
          <Button type="button" variant="outline" onClick={addConfessionTimeDay}>
            <Plus className="w-4 h-4 mr-2" />Add Day
          </Button>
          <Button type="button" onClick={() => saveParishInfoSection('confessionTimes', confessionTimes, 'confession')} disabled={savingInfo === 'confession'} className="bg-gold text-black hover:bg-gold/90">
            {savingInfo === 'confession' ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : 'Save Confession Times'}
          </Button>
        </div>
      </div>

      {/* Parish Staff Section */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-gold" />
          <h2 className="text-xl font-semibold font-display text-charcoal">Parish Staff</h2>
        </div>

        {parishStaff.length === 0 ? (
          <p className="text-charcoal-muted text-sm mb-4">No staff members added yet.</p>
        ) : (
          <div className="space-y-4 mb-4">
            {parishStaff.map((member, index) => (
              <div key={index} className="border border-surface-border rounded p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-charcoal-muted">Staff Member {index + 1}</span>
                  <button type="button" onClick={() => removeStaffMember(index)} className="p-1 text-red-500 hover:text-red-700 transition-colors" title="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-charcoal text-xs">Title *</Label>
                    <Input type="text" value={member.title} onChange={(e) => updateStaffMember(index, 'title', e.target.value)} placeholder="e.g. Pastor" className="mt-1 bg-surface-linen border-surface-border text-charcoal" />
                  </div>
                  <div>
                    <Label className="text-charcoal text-xs">Name *</Label>
                    <Input type="text" value={member.name} onChange={(e) => updateStaffMember(index, 'name', e.target.value)} placeholder="e.g. Fr. John Smith" className="mt-1 bg-surface-linen border-surface-border text-charcoal" />
                  </div>
                  <div>
                    <Label className="text-charcoal text-xs">Email</Label>
                    <Input type="email" value={member.email || ''} onChange={(e) => updateStaffMember(index, 'email', e.target.value)} placeholder="Optional" className="mt-1 bg-surface-linen border-surface-border text-charcoal" />
                  </div>
                  <div>
                    <Label className="text-charcoal text-xs">Phone</Label>
                    <Input type="tel" value={member.phone || ''} onChange={(e) => updateStaffMember(index, 'phone', e.target.value)} placeholder="Optional" className="mt-1 bg-surface-linen border-surface-border text-charcoal" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-surface-border">
          <Button type="button" variant="outline" onClick={addStaffMember}>
            <Plus className="w-4 h-4 mr-2" />Add Staff Member
          </Button>
          <Button type="button" onClick={() => saveParishInfoSection('parishStaff', parishStaff, 'staff')} disabled={savingInfo === 'staff'} className="bg-gold text-black hover:bg-gold/90">
            {savingInfo === 'staff' ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : 'Save Staff'}
          </Button>
        </div>
      </div>

      {/* Donation Dashboard */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold font-display text-charcoal">Donation Dashboard</h2>
            <p className="text-sm text-charcoal-muted mt-1">
              Totals show what the parish received, after platform and payment processing fees.
            </p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <select
              value={donationTypeFilter}
              onChange={(e) => setDonationTypeFilter(e.target.value)}
              className="bg-white border border-surface-border rounded px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="all">All Types</option>
              <option value="one-time">One-Time</option>
              <option value="recurring">Recurring</option>
              <option value="roundup">Round-Up</option>
            </select>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-white border border-surface-border rounded px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
        </div>

        {/* Custom Date Range Inputs */}
        {dateRange === 'custom' && (
          <div className="mb-6 p-4 bg-surface-linen rounded border border-surface-border">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-charcoal mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-surface-linen border-surface-border text-charcoal"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-charcoal mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-surface-linen border-surface-border text-charcoal"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Button
                  onClick={handleCustomRangeSubmit}
                  className="flex items-center gap-2"
                  disabled={!startDate || !endDate}
                >
                  Apply Filter
                </Button>
              </div>
            </div>
          </div>
        )}

        {dashboardLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : dashboard ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-surface-border rounded p-6">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign className="w-5 h-5 text-charcoal" />
                  <span className="text-xs text-charcoal-muted">All Time</span>
                </div>
                <div className="text-2xl font-bold text-charcoal mb-1">
                  {formatCurrency(dashboard.totalAmount)}
                </div>
                <div className="text-sm text-charcoal-muted">
                  {dashboard.totalCount} {dashboard.totalCount === 1 ? 'donation' : 'donations'}
                </div>
              </div>

              <div className="bg-white border border-surface-border rounded p-6">
                <div className="flex items-center justify-between mb-2">
                  <Users className="w-5 h-5 text-charcoal" />
                  <span className="text-xs text-charcoal-muted">Unique</span>
                </div>
                <div className="text-2xl font-bold text-charcoal mb-1">
                  {dashboard.uniqueDonors}
                </div>
                <div className="text-sm text-charcoal-muted">
                  {dashboard.uniqueDonors === 1 ? 'donor' : 'donors'}
                </div>
              </div>

              <div className="bg-white border border-surface-border rounded p-6">
                <div className="flex items-center justify-between mb-2">
                  <Calendar className="w-5 h-5 text-charcoal" />
                  <span className="text-xs text-charcoal-muted">This Month</span>
                </div>
                <div className="text-2xl font-bold text-charcoal mb-1">
                  {formatCurrency(dashboard.thisMonthAmount)}
                </div>
                <div className="text-sm text-charcoal-muted">
                  Avg: {formatCurrency(dashboard.averageDonationAmount)}
                </div>
              </div>

              <div className="bg-white border border-surface-border rounded p-6">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-charcoal" />
                  <span className="text-xs text-charcoal-muted">This Year</span>
                </div>
                <div className="text-2xl font-bold text-charcoal mb-1">
                  {formatCurrency(dashboard.thisYearAmount)}
                </div>
                <div className="text-sm text-charcoal-muted">
                  {dashboard.thisYearCount} {dashboard.thisYearCount === 1 ? 'donation' : 'donations'}
                </div>
              </div>
            </div>

            {/* Time Period Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white border border-surface-border rounded p-6">
                <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-charcoal" />
                  Today
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-charcoal-muted">Amount</span>
                    <span className="text-xl font-bold text-charcoal">{formatCurrency(dashboard.todayAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-charcoal-muted">Donations</span>
                    <span className="text-lg font-semibold text-charcoal">{dashboard.todayCount}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-surface-border rounded p-6">
                <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-charcoal" />
                  This Week
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-charcoal-muted">Amount</span>
                    <span className="text-xl font-bold text-charcoal">{formatCurrency(dashboard.thisWeekAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-charcoal-muted">Donations</span>
                    <span className="text-lg font-semibold text-charcoal">{dashboard.thisWeekCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Donations */}
            {dashboard.recentDonations && dashboard.recentDonations.length > 0 && (
              <div className="bg-white border border-surface-border rounded p-6">
                <h3 className="text-lg font-semibold text-charcoal mb-4">Recent Donations</h3>
                <div className="relative">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-surface-border">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-charcoal-muted">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-charcoal-muted">Donor</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-charcoal-muted">Gross</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-charcoal-muted">Fees</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-charcoal-muted">Net Received</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">Type</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(showAllDonations ? dashboard.recentDonations : dashboard.recentDonations.slice(0, VISIBLE_DONATION_COUNT)).map((donation) => (
                          <tr key={donation.id} className="border-b border-surface-border/50 hover:bg-surface-linen/50">
                            <td className="py-3 px-4 text-sm text-charcoal-muted whitespace-nowrap">
                              {formatDateTime(donation.completedAt || donation.createdAt)}
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <div className="text-sm font-medium text-charcoal">{donation.userName || 'Anonymous'}</div>
                                {donation.userEmail && (
                                  <div className="text-xs text-charcoal-muted">{donation.userEmail}</div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="text-sm text-charcoal-muted">{formatCurrency(donation.amount)}</div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="text-sm text-charcoal-muted">
                                {formatCurrency(donation.applicationFeeAmount ?? 0)}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="text-sm font-bold text-charcoal">
                                {formatCurrency(donation.netAmount ?? donation.amount)}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`inline-block px-2 py-1 text-xs rounded ${
                                donation.donationType === 'recurring'
                                  ? 'bg-gold/15 text-charcoal'
                                  : donation.donationType === 'roundup'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-gold/10 text-charcoal-muted'
                              }`}>
                                {donation.donationType === 'recurring' ? 'Recurring' : donation.donationType === 'roundup' ? 'Round-Up' : 'One-Time'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              {donation.paymentMethodLast4 ? (
                                <div className="flex items-center justify-center gap-1">
                                  <CreditCard className="w-4 h-4 text-charcoal-muted" />
                                  <span className="text-xs text-charcoal-muted">•••• {donation.paymentMethodLast4}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-charcoal-muted">N/A</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!showAllDonations && dashboard.recentDonations.length > VISIBLE_DONATION_COUNT && (
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  )}
                </div>
                {dashboard.recentDonations.length > VISIBLE_DONATION_COUNT && (
                  <button
                    onClick={() => setShowAllDonations(!showAllDonations)}
                    className="w-full mt-3 py-2 text-sm font-semibold text-gold hover:text-gold/80 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {showAllDonations ? (
                      <><ChevronUp className="w-4 h-4" />Show less</>
                    ) : (
                      <><ChevronDown className="w-4 h-4" />Show all {dashboard.recentDonations.length} donations</>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
            <p className="text-charcoal-muted">No donation data available.</p>
          </div>
        )}
      </div>
    </div>
  )
}
